/**
 * @packageDocumentation
 * @module      CloudApi.ts
 * @description Module to access the Cloud API that provides data for this application.
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
import Fetcher from "./Fetcher";
import System from "./System";
import SystemEx from "./SystemEx";
import RoboticNetworkSummary from "./RoboticNetworkSummary";
import RoboticNetwork from "./RoboticNetwork";
import AuditLogLine from "./AuditLogLine";
import RoboticNetworkDto from "./RoboticNetworkDto";
import CloudApiErrorEvent from "./CloudApiErrorEvent";
import { CorPageAuditLogs } from "./CorPageAuditLogs";
import LoggedInUser from "./LoggedInUser";
import { Role } from "./UserDetails";
import ConsoleLogger from "./ConsoleLogger";
/**
 * Module to access the Cloud API that provides data for this application.
 */
export default class CloudApi {
    getHeaders() {
        return {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + LoggedInUser.accessToken
        };
    }
    /**
     * Constructor.
     *
     * @param fetcher An object that mimics JavaScript's fetch function, or null to use fetch directly.
     *                For unit tests, use the Fetcher from this application.
     * @param element The element from which any errors should be dispatched. If undefined then error events will
     *                not be dispatched.
     */
    constructor(fetcher, element = undefined) {
        this.myFetcher = fetcher || new Fetcher();
        this.element = element;
    }
    /**
     * Make an RPC-style call. A good result returns a fulfilled Promise with nothing in it.
     * A bad result returns a rejected Promise with a reason. The server method must return the rejected
     * result in the body.
     *
     * @private
     */
    bareCall(httpVerb, url, body) {
        return this.myFetcher.fetch(url, {
            method: httpVerb,
            headers: this.getHeaders(),
            body: body
        }).then((response) => {
            if (response == null) {
                return Promise.reject('There was no response from the server.');
            }
            else if (response.ok) {
                return undefined; // OK
            }
            else {
                return response.text().then((txt) => {
                    return Promise.reject(txt || 'Unknown error.');
                });
            }
        }, (error) => {
            return Promise.reject('The server could not be reached.'); // unsuccessful call because of network failure
        });
    }
    getVersion() {
        const partialUrl = '/api/v1/version';
        const url = `${appConfig.sessionManagerBase}${partialUrl}`;
        return this.myFetcher.fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'text/plain',
                'Authorization': 'No Auth'
            },
            body: undefined
        }).then((response) => {
            return response.text();
        });
    }
    getItems(operation, partialUrl, itemFromJSON, queryString = "") {
        const url = `${appConfig.sessionManagerBase}${partialUrl}`;
        const emptyList = [];
        return this.myFetcher.fetch(url + queryString, {
            method: 'GET',
            headers: this.getHeaders(),
            body: undefined
        }).then((response) => {
            if (response.ok) {
                return response.json().then(r => {
                    if (this.isAuditLog(r)) {
                        CorPageAuditLogs.auditLogItemCount = r.auditLogItemCount;
                        r = r.auditLogItemList;
                    }
                    return r.map((json) => itemFromJSON(json));
                }, (error) => {
                    CloudApiErrorEvent.dispatch(this.element, operation, response, error);
                    return emptyList; // Response was not JSON
                });
            }
            else {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return emptyList; // response was not ok
            }
        }, (error) => {
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return emptyList; // unsuccessful call because of network failure
        });
    }
    isAuditLog(r) {
        return r.auditLogItemList != undefined;
    }
    /**
     * Get one item
     *
     * @param itemId The primary key of the item in the database.
     * @param operation A description of the operation being performed. This should fit naturally to fill in the
     * blank of this sentence: "There was an error while attempting to ____: etc."
     * @param partialUrl Something like "/api/v1/roboticNetwork"
     * @param itemFromJSON A function to construct a TItem from the JSON that the server returns.
     * @private
     */
    getItem(itemId, operation, partialUrl, itemFromJSON) {
        const url = `${appConfig.sessionManagerBase}${partialUrl}/${encodeURIComponent(itemId)}`;
        return this.myFetcher.fetch(url, {
            method: 'GET',
            headers: this.getHeaders(),
            body: undefined
        }).then((response) => {
            if (response.ok) {
                if (response.headers.get('Content-Length') === "0") {
                    return undefined; // Example: No associated system in call to getAssociatedSystem
                }
                return response.json().then(r => {
                    return itemFromJSON(r);
                }, (error) => {
                    ConsoleLogger.log(error);
                    CloudApiErrorEvent.dispatch(this.element, operation, response, error);
                    return undefined; // Response was not JSON
                });
            }
            else {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response was not ok
            }
        }, (error) => {
            ConsoleLogger.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    saveItem(item, operation, partialUrl, httpVerb) {
        const url = `${appConfig.sessionManagerBase}${partialUrl}`;
        return this.myFetcher.fetch(url, {
            method: httpVerb || 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(item)
        }).then((response) => {
            if (!response.ok) {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response was not ok
            }
            return response;
        }, (error) => {
            ConsoleLogger.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    /**
     * Delete an item.
     *
     * @param itemId The primary key of the item to delete.
     * @param operation A description of the operation being performed. This should fit naturally to fill in the
     * blank of this sentence: "There was an error while attempting to ____: etc."
     * @param partialUrl Something like "/api/v1/roboticNetwork"
     */
    deleteItem(itemId, operation, partialUrl) {
        const url = `${appConfig.sessionManagerBase}${partialUrl}/${encodeURIComponent(itemId.toString())}`;
        return this.myFetcher.fetch(url, {
            method: 'DELETE',
            headers: this.getHeaders(),
            body: undefined
        }).then((response) => {
            if (!response.ok) {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response was not ok
            }
            return response;
        }, (error) => {
            ConsoleLogger.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    /**
     * Get all systems from the database. Does not include devices.
     */
    getSystems() {
        return this.getItems("retrieve all systems", '/api/v1/systems', System.fromJSON);
    }
    getEmcSystems() {
        return this.getItems("retrieves all systems not already running EMC tests", '/api/v1/emc_test/emcSystems', System.fromJSON);
    }
    /**
     * Get a system, its devices, and its user.
     */
    getSystemEx(systemId) {
        const operation = 'retrieve the system';
        const url = `${appConfig.sessionManagerBase}/api/v1/systemEx/${encodeURIComponent(systemId)}`;
        return this.myFetcher.fetch(url, {
            method: 'GET',
            headers: this.getHeaders(),
            body: undefined
        }).then((response) => {
            if (response.ok) {
                return response.json().then(r => {
                    return SystemEx.fromJSON(r);
                }, (error) => {
                    ConsoleLogger.log(error);
                    CloudApiErrorEvent.dispatch(this.element, operation, response, error);
                    return undefined; // Response was not JSON
                });
            }
            else {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response was null or not ok
            }
        }, (error) => {
            ConsoleLogger.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    /**
     * Store a new extended system object.
     *
     * @param systemEx an encapsulation of a system, its devices, and its dedicated user.
     */
    createSystemEx(systemExDto) {
        return this.saveSystemEx(systemExDto, 'POST');
    }
    /**
     * Update an extended system object.
     *
     * @param systemEx an encapsulation of a system, its devices, and its dedicated user.
     */
    updateSystemEx(systemExDto) {
        return this.saveSystemEx(systemExDto, 'PUT');
    }
    saveSystemEx(systemExDto, httpVerb) {
        return this.saveItem(systemExDto, 'save the system', '/api/v1/systemEx', httpVerb);
    }
    /**
     * Get the system associated with the one identified by systemId, or return undefined if that system is not in
     * an association. The association may be pending, in the process of being rejected, etc.
     *
     * @param systemId The System ID of the system whose associated system you want to get.
     */
    getAssociatedSystem(systemId) {
        return this.getItem(systemId, "retrieve an associated system", '/api/v1/system/associated', System.fromJSON);
    }
    /**
     * Delete a system, cascade-deleting its devices and dedicated user.
     *
     * @param systemId The System ID of the system to delete.
     */
    deleteSystem(systemId) {
        return this.deleteItem(systemId, 'delete the system', '/api/v1/system');
    }
    getRoboticNetworkSummaries() {
        return this.getItems('retrieve all robotic networks', '/api/v1/roboticNetworkSummaries', RoboticNetworkSummary.fromJSON);
    }
    getRoboticNetworkSummariesForSystem(systemId) {
        const partialUrl = `/api/v1/roboticNetworkSummaries/systemId/${encodeURIComponent(systemId)}`;
        return this.getItems('retrieve summary data for robotic networks', partialUrl, RoboticNetworkSummary.fromJSON);
    }
    getRoboticNetwork(roboticNetworkId) {
        return this.getItem(roboticNetworkId, "retrieve the robotic network", '/api/v1/roboticNetwork', RoboticNetwork.fromJSON);
    }
    saveRoboticNetwork(roboticNetwork) {
        return this.saveItem(new RoboticNetworkDto(roboticNetwork), 'save the robotic network', '/api/v1/roboticNetwork');
    }
    /**
     * Delete a robotic network.
     *
     * @param roboticNetworkId The ID of the robotic network to delete.
     */
    deleteRoboticNetwork(roboticNetworkId) {
        return this.deleteItem(roboticNetworkId, 'delete the robotic network', '/api/v1/roboticNetwork');
    }
    generateNewPassword(element) {
        const url = `${appConfig.sessionManagerBase}/api/v1/newPassword`;
        const operation = "generate a new password";
        return this.myFetcher.fetch(url, {
            method: 'GET',
            headers: this.getHeaders()
        }).then((response) => {
            if (response.ok) {
                return response.json().then(r => {
                    return r.password;
                }, (error) => {
                    ConsoleLogger.log(error);
                    CloudApiErrorEvent.dispatch(this.element, operation, response, error);
                    return undefined; // Response was not JSON
                });
            }
            else {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response not ok
            }
        }, (error) => {
            ConsoleLogger.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    /**
     * Get an audit log.
     *
     * @param auditLogType The type of log.
     */
    getAuditLog(auditLogType, pageNo, itemsPerPage) {
        const url = '/api/v1/auditLog/' + auditLogType;
        return this.getItems('retrieve the audit log', url, AuditLogLine.fromJSON, `?itemsPerPage=${itemsPerPage}&pageNo=${pageNo}`);
    }
    fetchRolesFromAccessToken() {
        const operation = 'fetch the roles from access token';
        const url = `${appConfig.sessionManagerBase}/api/v1/user/fetchRoles`;
        return this.myFetcher.fetch(url, {
            method: 'GET',
            headers: this.getHeaders(),
            body: undefined
        }).then((response) => {
            if (response.ok) {
                return response.json().then(r => {
                    return r.map((json) => new Role(json.authority));
                }, (error) => {
                    ConsoleLogger.log("Something went wrong while fetching the roles. The response was not JSON. Error: " + error);
                    return undefined;
                });
            }
            else {
                ConsoleLogger.log("Something went wrong while fetching the roles. The response was null or not ok.");
                return undefined;
            }
        }, (error) => {
            ConsoleLogger.log("Something went wrong while fetching the roles. Unsuccessful call because of network failure. Error: " + error);
            return undefined;
        });
    }
    /*
    This below function is used to fetch the EMC data from the cloud backend
    */
    getEmcTestData(dataMap) {
        const url = `${appConfig.sessionManagerBase}/api/v1/emc_test/emcsignal`;
        const convertDataMapToJSON = (map) => {
            const obj = {};
            for (const [key, value] of map.entries()) {
                obj[key] = value;
            }
            return obj;
        };
        const requestData = convertDataMapToJSON(dataMap);
        ConsoleLogger.log("EMC signal call started");
        return this.myFetcher.fetch(url, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(requestData)
        }).then((response) => {
            ConsoleLogger.log("EMC signal call response returned");
            return response.json();
        }).catch((exception) => {
            ConsoleLogger.log("EMC signal call has an error. Exception: " + exception);
        });
    }
    /*
    This below function is used to stop fetching the EMC logs from the server
    */
    stopEMCLog(systemId) {
        const url = `${appConfig.sessionManagerBase}/api/v1/emc_test/stoplogs`;
        return this.myFetcher.fetch(url, {
            method: 'POST',
            headers: this.getHeaders(),
            body: systemId.toString()
        }).then((response) => {
            return response.text();
        });
    }
}
//# sourceMappingURL=CloudApi.js.map